import { z } from 'zod';

import { type FillDirection, FillDirectionType } from '../../../../schema/defs.ts';
import type { EvalResult } from '../../env.ts';
import { AmbiguousLiftTargetError, ShapeError } from '../../errors.ts';
import { type AstInput, astShape, nodePos, type OpCall, opCallPos } from '../../normalize.ts';
import { type ColumnRefInArg, opMeta, type PropagateSchemaCtx, type SpecialCtx } from '../../op-meta.ts';
import { kwargPath, mapValuePath, paramPath } from '../../path.ts';
import type { PlanNode } from '../../plan.ts';
import { fillColumnArray, materializedToRows } from '../../runtime/arquero.ts';
import { partitionByKeys } from '../../runtime/partition.ts';
import { astArg } from '../../schema/ast-codec.ts';
import {
  type ColumnNameAst,
  ColumnNameAstSchema,
  columnNameAstString,
  KeywordAstSchema,
} from '../../schema/keyword.ts';
import {
  type ColumnType,
  nestedDatasetCol,
  type Schema,
  schemaFromEntries,
  timestampCol,
  unknownCol,
} from '../../schema/types.ts';
import { type Cell, MaterializedDataset, synthesizeIdentity } from '../../value.ts';
import {
  type AggPairsMapEntry,
  aggContextScope,
  aggInputColumn,
  aggOutputEntries,
  aggPairsMapSchema,
  assertBareAggValueType,
  BARE_AGG_SYMBOLS,
  bareAggResultType,
  type CompiledAggPair,
  compileAggCallReducer,
  compileBareAgg,
  isBareAggSymbol,
} from './agg-funcs.ts';
import { bucketTimestamp, type Period, parseWindowBound, periodGrid, toDateValue } from './date-utils.ts';
import {
  classifyPerCell,
  columnNameAstList,
  columnNameAstName,
  liftPerCellOnColumns,
  requireColumns,
  resolveDsArg,
  vectorColRefs,
} from './helpers.ts';

/**
 * Interval-string → Period mapping. Single-unit only:
 *   `"1m"`→minute, `"1h"`→hour, `"1d"`→day, `"1w"`→week,
 *   `"1M"`→month, `"1q"`→quarter, `"1y"`→year.
 * Capital `M` for month resolves the ambiguity with minute. SSOT for
 * every consumer that needs to translate `time-bucket`'s positional
 * interval to a `Period` — including a source fetcher's `adaptPlan`
 * recognising a downstream push-down.
 */
export const INTERVAL_TO_PERIOD: Readonly<Record<string, Period>> = {
  '1m': 'minute',
  '1h': 'hour',
  '1d': 'day',
  '1w': 'week',
  '1M': 'month',
  '1q': 'quarter',
  '1y': 'year',
};

const INTERVAL_KEYS = Object.keys(INTERVAL_TO_PERIOD) as readonly (keyof typeof INTERVAL_TO_PERIOD & string)[];
const IntervalSchema = z.enum(INTERVAL_KEYS as readonly [string, ...string[]]);

/**
 * Per-cell `:agg` shape — non-empty vector of bare-agg keyword AST
 * nodes (`[:last :mean]`). Each keyword names a `BARE_AGG_SYMBOLS` entry;
 * the inner TS-ref `value` column is the implicit input.
 */
const PerCellAggSchema = z.array(KeywordAstSchema).min(1);

/**
 * `(time-bucket ds <col-or-cols> <interval> :group-by [:k…] :window [<startISO> <endISO>] :agg <agg-spec> :fill ffill|bfill)`
 *
 * Two forms, **auto-dispatched on the resolved col type(s)**:
 *
 *   - **Plain (row-level)**: a single timestamp col. Rows partition by
 *     `(bucket, :group-by…)`. Optional `:agg` is a map literal `{:out
 *     (agg-call …)}` — same shape as `aggregate :pairs` — reducing
 *     each group. Without `:agg`, output is the distinct
 *     `(bucket, :group-by…)` tuples.
 *   - **Per-cell (TS-ref)**: one or more nested-dataset (TS-ref) cols
 *     given as a single keyword or a keyword vector `[:a :b …]`. Each
 *     listed col's per-row cell is bucketed and reduced — the cell's
 *     inner `value` column is aggregated by the bare-keyword `:agg`
 *     vector `[:last :mean …]` (at least one). Output cells become
 *     nested-dataset `{bucket, <col>-<agg>…}`; the lifted columns are
 *     replaced in-place on the outer schema.
 *
 * Auto-dispatch rule: if ANY listed col is `nested-dataset`, the call is
 * the per-cell form; else it's plain. Mixing nested and plain cols in
 * one call is a `ShapeError`.
 *
 * Form-specific kwargs:
 *
 *   - `:group-by` — plain only (per-cell rejects with `ShapeError`).
 *   - `:fill ffill|bfill` — per-cell only; gap-fills empty `:window`
 *     buckets within each series after aggregation.
 *   - `:window [<start> <end>)` — both forms. Per-cell: emits the
 *     deterministic display-tz grid (gap buckets null-filled); plain:
 *     filters input rows to the half-open instant range.
 *   - `:agg` — required for per-cell (vector of bare keyword aggs);
 *     optional for plain (map literal `{:out (agg-call …)}` when
 *     present).
 *
 * Errors:
 *
 *   - First arg not a Dataset → `TypeMismatchError`.
 *   - A listed col isn't on `ds.schema` → `UnknownColumnRefError`.
 *   - A `:group-by` name isn't on `ds.schema` → `UnknownColumnRefError`.
 *   - Per-cell rejects `:group-by`, requires `:agg` (vector form), and
 *     rejects map `:agg` → `ShapeError`.
 *   - Plain rejects vector cols positional, rejects vector `:agg`, and
 *     rejects `:fill` → `ShapeError`.
 *   - Per-cell `:agg` symbol not in `BARE_AGG_SYMBOLS` → `ShapeError`.
 *   - Mixed nested + plain cols in one call → `ShapeError`.
 *
 * A `LazyDataset` input is forced via `ctx.materialize` before bucketing.
 */
export const timeBucket = z
  .object({
    name: z.literal('time-bucket'),
    params: z.tuple([
      astArg('<dataset>'),
      z.union([ColumnNameAstSchema, z.array(ColumnNameAstSchema).min(1)]),
      IntervalSchema,
    ]),
    'group-by': z.array(ColumnNameAstSchema).optional(),
    window: z.tuple([z.string().min(1), z.string().min(1)]).optional(),
    agg: z.union([PerCellAggSchema, aggPairsMapSchema(':agg')]).optional(),
    fill: FillDirectionType.optional(),
  })
  .describe(
    'Bucket rows by time. ALWAYS aggregates — it cannot return raw samples (use `unnest` for those). Plain form (scalar timestamp col): groups rows by `(bucket, :group-by…)`; `:agg` optional — without it, output is the distinct tuples. Per-cell form (TS-ref col(s) in a vector): buckets each cell and emits a nested-dataset column per input with inner `{bucket, <col>-<agg>…}` — always `unnest` afterwards and reference the renamed columns. `:window` is half-open `[start end)`; `:fill ffill|bfill` fills only null gap buckets — observations are never overwritten.',
  )
  .register(opMeta, {
    // Special evaluation: agg-call ops in plain `:agg` map values live
    // in `aggMeta`, NOT `opMeta` — eager dispatch would surface
    // UnknownOpError before apply. The dataset positional is evaluated
    // by hand via `ctx.evalNode`; col(s) arrive as keyword/string AST
    // shapes (extracted with `columnNameAstString`).
    evaluation: 'special',
    effect: 'pure',
    childScopes: [aggContextScope],
    docSignature:
      '(time-bucket ds :tsCol "1d" [:group-by [:k …]] [:agg {:out (agg-call :col) …}]) — plain form; (time-bucket ds [:refCol …] "1d" :window ["<startISO>" "<endISO>"] :agg [:min :max :mean] [:fill ffill|bfill]) — per-cell form; interval one of "1m"|"1h"|"1d"|"1w"|"1M"|"1q"|"1y"',
    example: {
      expr: '(-> wells (time-bucket [:oilProd :gasProd] "1w" :window ["2026-01-01" "2026-04-01"] :agg [:min :max :mean]))',
    },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const [dsAst, colsAst, interval] = data.params as readonly [
        AstInput,
        ColumnNameAst | readonly ColumnNameAst[],
        string,
      ];
      const { value: dsValue } = await ctx.evalNode(dsAst, ctx.env, paramPath(ctx.path, 0));
      const concrete = await resolveDsArg(dsValue, '(time-bucket ds <col> <interval> …)', ctx);

      const isVectorForm = Array.isArray(colsAst);
      const cols = (isVectorForm ? colsAst : [colsAst as ColumnNameAst]).map(columnNameAstString);
      const colsPosOf = (idx: number) =>
        nodePos(ctx.spans, isVectorForm ? paramPath(paramPath(ctx.path, 1), idx) : paramPath(ctx.path, 1));
      requireColumns(cols, concrete.schema, colsPosOf);

      const period = INTERVAL_TO_PERIOD[interval] as Period;
      const callPos = opCallPos(ctx.spans, ctx.path);
      const win = data.window as readonly [string, string] | undefined;

      const isPerCell = classifyPerCell(cols, concrete.schema, 'time-bucket', 'plain-timestamp', callPos);

      const value = await (isPerCell
        ? applyPerCell({
            concrete,
            cols,
            period,
            interval,
            win,
            aggAst: data.agg,
            fill: data.fill,
            groupBy: data['group-by'],
            ctx,
            callPos,
          })
        : applyPlain({
            concrete,
            cols,
            isVectorForm,
            period,
            win,
            aggAst: data.agg,
            fill: data.fill,
            groupBy: data['group-by'],
            ctx,
            callPos,
          }));
      return { value, env: ctx.env };
    },
    dataset: {
      // Auto-dispatch on resolved col types: any nested-dataset →
      // per-cell form (replace each lifted col with a nested-dataset
      // of inner bucket schema); otherwise plain form (bucket the
      // tsCol + group-by + agg outputs). Both shapes route through
      // their existing `propagate…Schema` SSOTs.
      propagateSchema: (data, ctx: PropagateSchemaCtx): Schema | undefined => {
        const params = (data as { readonly params?: readonly unknown[] }).params ?? [];
        const colsAst = params[1];
        const colsShape = astShape(colsAst);
        const cols =
          colsShape.kind === 'vector'
            ? columnNameAstList(colsAst)
            : ((): readonly string[] => {
                const nm = columnNameAstName(colsAst);
                return nm === undefined ? [] : [nm];
              })();
        if (cols.length === 0) {
          return undefined;
        }
        const colKinds = cols.map((c) => ctx.inputSchema.get(c)?.kind);
        const isPerCell = colKinds.some((k) => k === 'nested-dataset');
        const kwargs = data as {
          readonly 'group-by'?: unknown;
          readonly agg?: unknown;
        };
        if (isPerCell) {
          const aggShape = astShape(kwargs.agg);
          const bareSyms =
            aggShape.kind === 'vector'
              ? aggShape.elements.flatMap((el) => {
                  const s = astShape(el);
                  return s.kind === 'keyword' ? [s.name] : [];
                })
              : [];
          return propagatePerCellSchema(ctx.inputSchema, cols, bareSyms);
        }
        const groupBy = columnNameAstList(kwargs['group-by']);
        const aggMap = (kwargs.agg as { readonly $map?: readonly AggPairsMapEntry[] } | undefined)?.$map ?? [];
        return propagatePlainSchema(ctx.inputSchema, cols[0] as string, groupBy, aggMap);
      },
      // Cols (positional 1) + `:group-by` cols + each plain-form
      // `:agg` pair's input col. Per-cell `:agg` keywords are bare-agg
      // symbols, not column refs.
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
        const params = (data as { readonly params?: readonly unknown[] }).params ?? [];
        const colsAst = params[1];
        const colsShape = astShape(colsAst);
        const colRefs: readonly ColumnRefInArg[] =
          colsShape.kind === 'vector'
            ? vectorColRefs(colsAst, paramPath(basePath, 1))
            : ((): readonly ColumnRefInArg[] => {
                const nm = columnNameAstName(colsAst);
                return nm === undefined ? [] : [{ name: nm, path: paramPath(basePath, 1) }];
              })();
        const kwargs = data as { readonly 'group-by'?: unknown; readonly agg?: unknown };
        const groupRefs = vectorColRefs(kwargs['group-by'], kwargPath(basePath, 'group-by'));
        const aggMap = (kwargs.agg as { readonly $map?: readonly AggPairsMapEntry[] } | undefined)?.$map ?? [];
        const aggRefs = aggMap.flatMap(({ k, v }, i) => {
          const name = aggInputColumn(v as { readonly params?: readonly unknown[] });
          return name === undefined
            ? []
            : [{ name, path: `${mapValuePath(kwargPath(basePath, 'agg'), k, i)}/params/0` }];
        });
        return [...colRefs, ...groupRefs, ...aggRefs];
      },
    },
  });

// Plain-timestamp form.

/**
 * Plain row-level form. Each input row is assigned to the bucket its
 * `tsCol` value falls in; rows partition by `(bucket, :group-by…)`. If
 * `:agg` is present, each group is reduced through `compileAggCallReducer`
 * (one entry per map pair); without `:agg`, the output is the distinct
 * `(bucket, :group-by…)` tuples.
 *
 * `:fill ffill|bfill` is accepted when `:window` is also set — the full
 * bucket grid is emitted (gap buckets null in every `:agg` output column)
 * and fill is applied per `(:group-by …)` series independently.
 * Observations are never overwritten — only null gap buckets are filled.
 * Without `:window` there is no deterministic grid to gap-fill against,
 * so plain `:fill` requires `:window` (and the {@link ShapeError} below
 * tells the caller exactly what to do).
 *
 * Plain form rejects vector cols positional and vector `:agg`. Rejects
 * `:fill` only when `:window` is absent.
 *
 * Delegates the bucket-assign / group / reduce / grid-emit / per-series
 * fill loop to {@link bucketReduceWithFill} — the SSOT shared with the
 * per-cell form. `applyPlain` owns only the AST-shape guards, the
 * plain-form `:agg` map compilation, and the outer-schema wrapping.
 */
const applyPlain = async (args: {
  readonly concrete: MaterializedDataset;
  readonly cols: readonly string[];
  readonly isVectorForm: boolean;
  readonly period: Period;
  readonly win: readonly [string, string] | undefined;
  readonly aggAst: unknown;
  readonly fill: FillDirection | undefined;
  readonly groupBy: readonly ColumnNameAst[] | undefined;
  readonly ctx: SpecialCtx;
  readonly callPos: ReturnType<typeof opCallPos>;
}): Promise<MaterializedDataset> => {
  const { concrete, cols, isVectorForm, period, win, aggAst, fill, groupBy, ctx, callPos } = args;
  if (isVectorForm) {
    throw new ShapeError(
      'the `[col …]` vector positional of time-bucket is only valid on TS-ref (nested-dataset) columns; pass a single timestamp column for row-level bucketing',
      callPos,
      '#1',
    );
  }
  if (fill !== undefined && win === undefined) {
    throw new ShapeError(
      ':fill on the plain-timestamp form of time-bucket requires :window — without a window there is no deterministic grid to gap-fill against. Either add :window ["<startISO>" "<endISO>"], or move bucketing into the per-cell form (vector positional on TS-ref columns): (time-bucket ds [:col …] …)',
      callPos,
      ':fill',
    );
  }
  const aggPairs = parsePlainAggMap(aggAst, callPos);
  const groupByCols = (groupBy ?? []).map(columnNameAstString);
  requireColumns(groupByCols, concrete.schema, (i) =>
    nodePos(ctx.spans, paramPath(kwargPath(ctx.path, 'group-by'), i)),
  );

  const tsCol = cols[0] as string;
  const compiled: readonly CompiledAggPair[] = await Promise.all(
    aggPairs.map(async ({ k, v }, i) => ({
      outName: k,
      reduce: await compileAggCallReducer(v, mapValuePath(kwargPath(ctx.path, 'agg'), k, i), ctx.spans),
    })),
  );

  // Gap-fill semantics: when the caller asked for `:fill`, emit the full
  // window grid (so gap buckets exist to be filled). Otherwise keep the
  // historical observed-only output — `:window` alone remains a filter,
  // never a grid.
  const gridKeys = fill !== undefined && win !== undefined ? periodGrid(win[0], win[1], period) : undefined;

  const outputCols = bucketReduceWithFill({
    rows: materializedToRows(concrete),
    tsCol,
    period,
    win,
    gridKeys,
    partitionCols: groupByCols,
    pairs: compiled,
    fill,
    bucketOutName: tsCol,
  });
  // SYNTHESIS: each output row is a `(bucket × :group-by)` cell. It keeps
  // the group's single instance identity IFF `:group-by` includes both
  // `space` and `externalId` (then both survive verbatim as partition
  // columns, repeated across that instance's buckets); else undefined.
  return new MaterializedDataset(
    outputCols,
    propagatePlainSchema(concrete.schema, tsCol, groupByCols, aggPairs),
    synthesizeIdentity(outputCols, groupByCols, concrete.schema),
  );
};

/**
 * Build a `(epochMs) => boolean` membership predicate for a half-open
 * `[start, end)` window. `undefined` `win` yields a constant `true`
 * predicate, so the same kernel handles both windowed and unwindowed
 * filters without an `if` branch at the call site.
 */
const makeWindowFilter = (win: readonly [string, string] | undefined): ((ms: number) => boolean) => {
  if (win === undefined) {
    return () => true;
  }
  const start = parseWindowBound(win[0]);
  const stop = parseWindowBound(win[1]);
  const startMs = start === null ? Number.POSITIVE_INFINITY : start.getTime();
  const stopMs = stop === null ? Number.NEGATIVE_INFINITY : stop.getTime();
  return (ms) => ms >= startMs && ms < stopMs;
};

/**
 * Validate `:agg` is the plain-form map shape and return the map entries.
 * Returns an empty array when `:agg` is omitted (plain-form `:agg` is
 * optional). Rejects the per-cell vector shape with a precise ShapeError.
 */
const parsePlainAggMap = (aggAst: unknown, callPos: ReturnType<typeof opCallPos>): readonly AggPairsMapEntry[] => {
  if (aggAst === undefined) {
    return [];
  }
  if (Array.isArray(aggAst)) {
    throw new ShapeError(
      'plain-timestamp time-bucket requires a map-literal `:agg {:out (agg-call …)}`; the bare-keyword vector form is per-cell (TS-ref) only',
      callPos,
      ':agg',
    );
  }
  // `:agg` was already validated against `aggPairsMapSchema(':agg')` by the
  // op-schema union at dispatch (the `agg:` field of the time-bucket schema).
  // A non-undefined, non-array value here is therefore guaranteed the strict
  // `{$map}` map form — read it directly, exactly as `aggregate` does with
  // its `:pairs` field, instead of re-parsing it once per call.
  return (aggAst as { readonly $map: readonly AggPairsMapEntry[] }).$map;
};

/**
 * Plain-form schema: bucketed `tsCol` is timestamp, `:group-by` keys
 * keep their input type, agg outputs typed via the shared SSOT
 * `aggOutputEntries`.
 */
const propagatePlainSchema = (
  inputSchema: Schema,
  tsCol: string,
  groupByCols: readonly string[],
  aggPairs: readonly AggPairsMapEntry[],
): Schema => {
  const groupEntries = groupByCols.map((c) => [c, inputSchema.get(c) as ColumnType] as const);
  // Adapt map entries to the (name, params[<agg-call>]) shape expected
  // by `aggOutputEntries` — symmetric with the aggregate-op adapter.
  const adapted = aggPairs.map(({ k, v }) => ({ name: k, params: [v] as readonly unknown[] }));
  return schemaFromEntries([[tsCol, timestampCol], ...groupEntries, ...aggOutputEntries(adapted, inputSchema)]);
};

/**
 * SSOT for the translated cell-local plan node chain that
 * {@link applyPerCell} records onto each lazy timeseries cell. Two nodes:
 *
 *   1. `(time-bucket cell :timestamp <interval> :window … :fill …
 *      :agg {:<col>-<sym> (<sym> :value)…})` — the plain-form bucket
 *      on the cell's actual schema (`:timestamp`/`:value`). Output
 *      schema is `{:timestamp, <col>-<sym>…}`.
 *   2. `(rename ds :timestamp :bucket)` — project the bucket col to
 *      the per-cell convention's `:bucket` name so the cell's declared
 *      `perCellInnerSchema` matches what the replay produces and
 *      downstream `unnest`/`:pick` against `:bucket` works.
 *
 * The recorded `params` are stored with the dataset positional
 * stripped (the {@link PlanNode} contract). `liftColumn` is omitted —
 * the cell-local form is standard plain-form dispatch on the cell, not
 * a per-cell re-entry.
 *
 * Symmetrical with the timeseries provider's `adaptPlan`, which
 * recognises this `time-bucket` node and absorbs its
 * `{window, period, aggs}` as a push-down hint; the `rename` node is a
 * pure post-projection and ignored by the adapter.
 */
const buildPerCellTimeBucketPlanNode = (args: {
  readonly col: string;
  readonly bareSyms: readonly string[];
  readonly interval: string;
  readonly win: readonly [string, string] | undefined;
  readonly fill: FillDirection | undefined;
  readonly innerSchemaAfterBucket: Schema;
  readonly innerSchemaAfterRename: Schema;
}): readonly PlanNode[] => {
  const { col, bareSyms, interval, win, fill, innerSchemaAfterBucket, innerSchemaAfterRename } = args;
  const TimestampKw = { $kw: 'timestamp' } as const;
  const ValueKw = { $kw: 'value' } as const;
  const BucketKw = { $kw: 'bucket' } as const;
  // Bare-agg convention: `count` is zero-arg; every other bare-eligible
  // agg takes `:value`. Same convention as `bareAggArgs` in agg-funcs.
  const aggMap = {
    $map: bareSyms.map((sym) => ({
      k: `${col}-${sym}`,
      v: { name: sym, params: sym === 'count' ? [] : [ValueKw] },
    })),
  };
  const bucketArgs: OpCall = {
    name: 'time-bucket',
    params: [TimestampKw, interval],
    ...(win === undefined ? {} : { window: win }),
    ...(fill === undefined ? {} : { fill }),
    agg: aggMap,
  };
  const renameArgs: OpCall = {
    name: 'rename',
    params: [TimestampKw, BucketKw],
  };
  return [
    { op: 'time-bucket', args: bucketArgs, schemaAfter: innerSchemaAfterBucket },
    { op: 'rename', args: renameArgs, schemaAfter: innerSchemaAfterRename },
  ];
};

// Per-cell (TS-ref) form.

/**
 * Per-cell form. For each listed col, builds a {@link liftPerCellOnColumns}
 * spec that:
 *
 *   - For a lazy cell, appends a translated plan-node CHAIN to the
 *     cell's plan: `(time-bucket cell :timestamp <interval> :window …
 *     :agg {:<col>-<sym> (<sym> :value)})` followed by `(rename ds
 *     :timestamp :bucket)` so the cell's actual replay output matches
 *     the per-cell convention's `{bucket, <col>-<sym>}` inner schema.
 *     The bucket-and-reduce runs at materialisation time via the
 *     standard replay path; a ts-ref source's `adaptPlan` absorbs the
 *     `time-bucket` node as a push-down hint at fetch time.
 *   - For an already-materialised cell, runs `bucketAndReduceCell`
 *     eagerly with the compiled `pairs` — the data is in hand.
 *
 * Both paths produce the same `{bucket, <col>-<agg>…}` inner schema.
 *
 * Rejects `:group-by`, requires `:agg` (vector form), and rejects map
 * `:agg`.
 */
const applyPerCell = async (args: {
  readonly concrete: MaterializedDataset;
  readonly cols: readonly string[];
  readonly period: Period;
  readonly interval: string;
  readonly win: readonly [string, string] | undefined;
  readonly aggAst: unknown;
  readonly fill: FillDirection | undefined;
  readonly groupBy: readonly ColumnNameAst[] | undefined;
  readonly ctx: SpecialCtx;
  readonly callPos: ReturnType<typeof opCallPos>;
}): Promise<MaterializedDataset> => {
  const { concrete, cols, period, interval, win, aggAst, fill, groupBy, ctx, callPos } = args;
  if (groupBy !== undefined && groupBy.length > 0) {
    throw new ShapeError(':group-by is not valid on the per-cell (TS-ref) form of time-bucket', callPos, ':group-by');
  }
  const bareSyms = parsePerCellAggVector(aggAst, callPos);

  const specs = await Promise.all(
    cols.map(async (col) => {
      // Per-cell agg/time-bucket operates on the cell's `value` axis, not
      // a positional column: resolve its real type (string vs numeric)
      // and reject a multi-aggregate cell that has no single value axis.
      const valueType = perCellValueType(col, concrete.schema.get(col) as ColumnType, callPos);
      // A numeric-only agg (`sum`/`mean`) over a concrete string value is
      // a type error; over `unknown` (top-level datapoints) it defers to
      // materialisation. Type-agnostic aggs accept any value type.
      bareSyms.forEach((s) => {
        assertBareAggValueType(s, valueType, callPos);
      });
      const pairs = await Promise.all(bareSyms.map((s) => compileBareAgg(s, col, ctx.spans)));
      const innerSchemaAfterRename = perCellInnerSchema(col, bareSyms, valueType);
      // Intermediate schema after the plain-form time-bucket node (tsCol
      // name preserved); the trailing rename projects it to the per-cell
      // convention's `:bucket`.
      const innerSchemaAfterBucket = perCellInnerSchema(col, bareSyms, valueType, 'timestamp');
      return {
        col,
        innerSchemaAfter: innerSchemaAfterRename,
        cellPlanNodes: (_innerSchemaBefore: Schema): readonly PlanNode[] =>
          buildPerCellTimeBucketPlanNode({
            col,
            bareSyms,
            interval,
            win,
            fill,
            innerSchemaAfterBucket,
            innerSchemaAfterRename,
          }),
        cellFn: async (innerDs: MaterializedDataset): Promise<MaterializedDataset> =>
          bucketAndReduceCell(innerDs, period, win, pairs, fill, col, bareSyms),
      };
    }),
  );

  return liftPerCellOnColumns(concrete, specs, ctx);
};

/**
 * Bucket a single inner TS-ref cell by `period`, optionally restricted
 * to `:window`, reducing each kept bucket through the precompiled bare-
 * agg `pairs`. Output is a `MaterializedDataset` whose schema is the
 * per-cell inner schema (`{bucket, <col>-<agg>…}`).
 *
 * Thin wrapper over the shared {@link bucketReduceWithFill} kernel —
 * passes `partitionCols: []` (each cell is one observed series) and
 * `gridKeys` derived from `:window` (per-cell always emits the grid
 * when a window is set, so gap buckets are null-filled before per-series
 * ffill/bfill runs).
 */
const bucketAndReduceCell = (
  cell: MaterializedDataset,
  period: Period,
  win: readonly [string, string] | undefined,
  pairs: readonly CompiledAggPair[],
  fill: FillDirection | undefined,
  col: string,
  bareSyms: readonly string[],
): MaterializedDataset => {
  const gridKeys = win === undefined ? undefined : periodGrid(win[0], win[1], period);
  const cols = bucketReduceWithFill({
    rows: materializedToRows(cell),
    tsCol: 'timestamp',
    period,
    win,
    gridKeys,
    partitionCols: [],
    pairs,
    fill,
    bucketOutName: 'bucket',
  });
  // Result-type the per-cell output against the cell's REAL `value` axis
  // (a string series' `(last :value)` stays string); fall back to
  // `unknown` if the cell carries no `value` column.
  const valueType = cell.schema.get('value') ?? unknownCol;
  return new MaterializedDataset(cols, perCellInnerSchema(col, bareSyms, valueType));
};

/**
 * The canonical value axis of a per-cell timeseries column — the inner
 * `value` column a bare agg reduces. A raw timeseries cell carries one
 * such column; the per-cell agg/`time-bucket` operates on it (NOT on a
 * positional `cols[0]`), so its type drives both the result typing and
 * the numeric-agg guard.
 */
const PER_CELL_VALUE_AXIS = 'value';

/**
 * Resolve the value-column type of a nested-dataset (timeseries) column
 * to feed per-cell aggregation. A raw timeseries cell's inner schema has
 * a single `value` axis; a cell that was ALREADY bucketed into multiple
 * aggregate columns has no single `value`, so re-aggregating it is
 * `AmbiguousLiftTargetError` (the value axis is undefined). The col is a
 * precondition `nested-dataset` (the dispatcher validated it).
 */
const perCellValueType = (col: string, colType: ColumnType, callPos: ReturnType<typeof opCallPos>): ColumnType => {
  const inner = colType.kind === 'nested-dataset' ? colType.innerSchema : undefined;
  const valueType = inner?.get(PER_CELL_VALUE_AXIS);
  if (valueType === undefined) {
    throw new AmbiguousLiftTargetError(col, inner === undefined ? [] : [...inner.keys()], callPos);
  }
  return valueType;
};

/**
 * Per-cell inner schema for one lifted col: a timestamp key column +
 * one column per bare agg, named `<col>-<agg>`, typed via the SSOT
 * `bareAggResultType` against the cell's REAL value type (so a string
 * series' `(last :value)` is `string`, not forced numeric). `keyCol`
 * defaults to the per-cell convention `:bucket`; the lift-translate path
 * uses `:timestamp` for the intermediate node-after-bucket schema before
 * the trailing `rename` projects it to `:bucket`.
 */
const perCellInnerSchema = (
  col: string,
  bareSyms: readonly string[],
  valueType: ColumnType,
  keyCol = 'bucket',
): Schema =>
  schemaFromEntries([
    [keyCol, timestampCol],
    ...bareSyms.map((s) => [`${col}-${s}`, bareAggResultType(s, valueType)] as const),
  ]);

/**
 * Per-cell outer schema: replace each lifted col with a nested-dataset
 * of `perCellInnerSchema` typed against that col's own inner `value`
 * axis; keep every other col verbatim. A lifted col with no resolvable
 * value axis (a non-nested col, or an already-multi-aggregate cell)
 * falls back to a `value:unknown` axis so the static pass stays total —
 * the precise `AmbiguousLiftTargetError` surfaces at op-apply, where a
 * source position is available.
 */
const propagatePerCellSchema = (inputSchema: Schema, cols: readonly string[], bareSyms: readonly string[]): Schema => {
  const liftSet = new Set(cols);
  return schemaFromEntries(
    Array.from(inputSchema.entries()).map(([k, t]) => {
      if (!liftSet.has(k)) {
        return [k, t] as const;
      }
      const valueType = t.kind === 'nested-dataset' ? (t.innerSchema.get('value') ?? unknownCol) : unknownCol;
      return [k, nestedDatasetCol(perCellInnerSchema(k, bareSyms, valueType))] as const;
    }),
  );
};

/**
 * Validate `:agg` is the per-cell keyword-vector shape (required, at
 * least one entry, each a bare-agg keyword) and return the bare-agg
 * symbols. Rejects the plain-form map shape with a precise ShapeError.
 */
const parsePerCellAggVector = (aggAst: unknown, callPos: ReturnType<typeof opCallPos>): readonly string[] => {
  if (aggAst === undefined) {
    throw new ShapeError(
      'per-cell (TS-ref) time-bucket requires `:agg [:last :mean …]` — at least one bare-agg keyword',
      callPos,
      ':agg',
    );
  }
  if (!Array.isArray(aggAst)) {
    throw new ShapeError(
      'per-cell time-bucket requires a vector of bare-agg keywords `:agg [:last :mean]`; the map-literal form is plain-timestamp only',
      callPos,
      ':agg',
    );
  }
  const parsed = PerCellAggSchema.safeParse(aggAst);
  if (!parsed.success) {
    throw new ShapeError(
      'per-cell `:agg` must be a vector of bare-agg keywords (e.g. `[:last :mean :sum]`)',
      callPos,
      ':agg',
    );
  }
  const syms = parsed.data.map((k) => k.$kw);
  const bad = syms.find((s) => !isBareAggSymbol(s));
  if (bad !== undefined) {
    throw new ShapeError(
      `unknown aggregate \`${bad}\`; expected one of ${BARE_AGG_SYMBOLS.join(', ')}`,
      callPos,
      ':agg',
    );
  }
  return syms;
};

// Shared bucket-reduce kernel.

/**
 * A row paired with its assigned bucket. The bucket rides *alongside* the
 * row rather than being spliced *into* it (`{ ...row, __bucketed__ }`), so a
 * wide row costs one 2-field wrapper instead of a full per-column shallow
 * copy — the dominant allocation when bucketing many wide rows.
 */
type BucketedRow = { readonly row: Record<string, unknown>; readonly bucket: Date };

/**
 * Unified bucket assignment for both forms of time-bucket. SSOT
 * replacing the old per-form `bucketRowsByPeriod` (plain) and
 * `assignCellBuckets` (per-cell) — same logic, single function.
 *
 * Pairs each row with the Date bucket its `tsCol` value buckets into. Drops
 * rows whose timestamp is null or outside `[win[0], win[1])`.
 *
 * With `gridKeys` set (caller passes a `periodGrid` for the window), each
 * row's bucket is the grid key it snaps to via {@link gridBucketIndex} —
 * deterministic even when window bounds don't fall on period boundaries.
 * Without `gridKeys`, the bucket is `bucketTimestamp(row[tsCol], period)`
 * with the window-bounds filter from {@link makeWindowFilter}. Pure: no
 * mutation of `rows`, and the row records are passed through by reference
 * (never cloned).
 */
const assignBuckets = (
  rows: readonly Record<string, unknown>[],
  tsCol: string,
  period: Period,
  win: readonly [string, string] | undefined,
  gridKeys: readonly Date[] | undefined,
): readonly BucketedRow[] => {
  if (gridKeys === undefined) {
    const inWindow = makeWindowFilter(win);
    return rows.reduce<BucketedRow[]>((acc, row) => {
      const d = toDateValue(row[tsCol]);
      if (d === null || !inWindow(d.getTime())) {
        return acc;
      }
      const b = bucketTimestamp(row[tsCol], period);
      if (b !== null) {
        acc.push({ row, bucket: b });
      }
      return acc;
    }, []);
  }
  const stop = parseWindowBound(win?.[1]);
  const stopMs = stop === null ? -1 : stop.getTime();
  const gridMs = gridKeys.map((d) => d.getTime());
  return rows.reduce<BucketedRow[]>((acc, row) => {
    const d = toDateValue(row[tsCol]);
    if (d === null) {
      return acc;
    }
    const i = gridBucketIndex(d.getTime(), gridMs, stopMs);
    if (i >= 0) {
      acc.push({ row, bucket: gridKeys[i] as Date });
    }
    return acc;
  }, []);
};

/**
 * Shared bucket-reduce-fill kernel used by both forms of time-bucket.
 *
 * Pipeline:
 *
 *   1. Pair each row with its Date bucket via {@link assignBuckets} —
 *      drops null/out-of-window rows.
 *   2. Partition by `partitionCols` — one observed series per partition
 *      tuple. `[]` (per-cell convention) yields a single series.
 *   3. For each series: group rows by bucket and reduce each bucket
 *      through `pairs`. When `gridKeys` is set, the full grid is emitted
 *      (gap buckets null-filled); otherwise observed buckets ascending.
 *   4. When `fill` is set, apply {@link fillColumnArray} per agg-output
 *      column within each series — observations are never overwritten,
 *      only null gap buckets are filled.
 *
 * Output is column-major: `bucketOutName`, then each `partitionCols`
 * entry, then each `pairs.outName` entry. The caller wraps with the
 * appropriate {@link Schema}.
 *
 * Does not mutate `rows` or `pairs`. The series computation is pure; the final
 * column-major assembly is a deliberate imperative carve-out (see the inline
 * note) that preallocates each output column and fills it by index to avoid the
 * per-series intermediate arrays a `flatMap` chain would allocate on this hot
 * path.
 */
const bucketReduceWithFill = (args: {
  readonly rows: readonly Record<string, unknown>[];
  readonly tsCol: string;
  readonly period: Period;
  readonly win: readonly [string, string] | undefined;
  readonly gridKeys: readonly Date[] | undefined;
  readonly partitionCols: readonly string[];
  readonly pairs: readonly CompiledAggPair[];
  readonly fill: FillDirection | undefined;
  readonly bucketOutName: string;
}): Record<string, Cell[]> => {
  const { rows, tsCol, period, win, gridKeys, partitionCols, pairs, fill, bucketOutName } = args;
  const bucketed = assignBuckets(rows, tsCol, period, win, gridKeys);

  const aggNames = pairs.map((p) => p.outName);

  // Partition by partitionCols — one observed series per partition tuple.
  // `[]` yields a single series (per-cell convention: each cell is one
  // implicit series, no outer grouping required). Extractors read the
  // partition cols off the wrapped row; `keys` carries the raw partition
  // values for the output (no `representativeRow` record needed).
  const seriesGroups =
    partitionCols.length === 0
      ? [{ keys: [] as readonly unknown[], items: bucketed }]
      : partitionByKeys(
          bucketed,
          partitionCols.map((c) => (w: BucketedRow) => w.row[c] ?? null),
        );

  type Series = {
    readonly partitionValues: readonly Cell[];
    readonly bucketDates: readonly Date[];
    readonly aggValues: readonly (readonly Cell[])[];
  };
  const series: readonly Series[] = seriesGroups.map(({ keys: partitionValues, items: seriesRows }): Series => {
    // Group by bucket instant directly into a Map keyed on epoch-ms. The
    // bucket is already a `Date` we hold, so there's no need for the
    // structural `cellKey` / RowGroup / representativeRow machinery — a
    // get-or-push reduce both partitions and records each bucket's first
    // `Date` (for the observed-keys ordering below). Values are the original
    // row records, handed to the reducers untouched.
    const byMs = seriesRows.reduce<Map<number, { readonly date: Date; readonly rows: Record<string, unknown>[] }>>(
      (m, w) => {
        const ms = w.bucket.getTime();
        const bucket = m.get(ms);
        if (bucket === undefined) {
          m.set(ms, { date: w.bucket, rows: [w.row] });
        } else {
          bucket.rows.push(w.row);
        }
        return m;
      },
      new Map(),
    );
    // With `gridKeys`, emit the deterministic grid (gap buckets
    // null-filled); without it, the observed buckets ascending.
    const orderedKeys: readonly Date[] =
      gridKeys ?? Array.from(byMs.values(), (b) => b.date).sort((a, b) => a.getTime() - b.getTime());
    const reduced: readonly (readonly Cell[])[] = pairs.map(({ reduce }) =>
      orderedKeys.map((k) => {
        const groupRows = byMs.get(k.getTime())?.rows ?? [];
        return (groupRows.length > 0 ? reduce(groupRows) : null) as Cell;
      }),
    );
    const filled: readonly (readonly Cell[])[] =
      fill === undefined ? reduced : reduced.map((arr) => fillColumnArray(arr, fill));
    return {
      partitionValues: partitionCols.map((_, i) => (partitionValues[i] ?? null) as Cell),
      bucketDates: orderedKeys,
      aggValues: filled,
    };
  });

  // imperative (CLAUDE.md side-effect carve-out, pre-approved): concatenate
  // series into column-major output in a single pass. Each series contributes
  // `bucketDates.length` consecutive rows. Preallocate every output column to
  // the total row count and fill by index — this avoids the per-series
  // intermediate arrays (and partition `.map(() => const)` repeat-arrays) a
  // flatMap chain would allocate on this hot path. Inputs (`rows`/`pairs`) are
  // never mutated; only the freshly allocated output arrays are filled.
  const total = series.reduce((sum, s) => sum + s.bucketDates.length, 0);
  const out: Record<string, Cell[]> = { [bucketOutName]: new Array<Cell>(total) };
  const bucketColumn = out[bucketOutName] as Cell[];
  const partOut = partitionCols.map((c) => {
    const col = new Array<Cell>(total);
    out[c] = col;
    return col;
  });
  const aggOut = aggNames.map((n) => {
    const col = new Array<Cell>(total);
    out[n] = col;
    return col;
  });
  let w = 0;
  for (const s of series) {
    const len = s.bucketDates.length;
    for (let r = 0; r < len; r++) {
      bucketColumn[w + r] = s.bucketDates[r] as Cell;
    }
    // Partition values are constant across the series — range-fill.
    partOut.forEach((col, i) => {
      col.fill(s.partitionValues[i] as Cell, w, w + len);
    });
    aggOut.forEach((col, i) => {
      const av = s.aggValues[i] as readonly Cell[];
      for (let r = 0; r < len; r++) {
        col[w + r] = av[r] as Cell;
      }
    });
    w += len;
  }
  return out;
};

// Pure numeric grid bucketing (shared with time-bucket.test.ts).

/**
 * Index of the half-open grid bucket a datapoint instant `t`
 * (epoch-ms) belongs to: the greatest `i` with `gridMs[i] <= t`,
 * valid only inside the window `[gridMs[0], stopMs)`. Returns `-1`
 * when `t` is outside the window (datapoint dropped) or the grid is
 * empty. Pure recursive binary search (`gridMs` is ascending) —
 * behaviour-identical to the old "bucketTimestamp(t) ∈ gridSet"
 * membership test because the grid is exactly the contiguous display-
 * zone period starts. Tail-shape recursion bounded by `O(log n)` —
 * the JS stack handles a million grid keys without issue.
 */
export const gridBucketIndex = (t: number, gridMs: readonly number[], stopMs: number): number => {
  const last = gridMs.length - 1;
  if (last < 0 || t < (gridMs[0] as number) || t >= stopMs) {
    return -1;
  }
  return floorIndex(t, gridMs, 0, last);
};

/**
 * Recursive binary-search core for `gridBucketIndex`: greatest `i` in
 * `[lo, hi]` with `gridMs[i] <= t`. Pure — every step returns a fresh
 * `(lo, hi)` pair, no mutation.
 */
const floorIndex = (t: number, gridMs: readonly number[], lo: number, hi: number): number => {
  if (lo === hi) {
    return lo;
  }
  const mid = (lo + hi + 1) >> 1;
  return (gridMs[mid] as number) <= t ? floorIndex(t, gridMs, mid, hi) : floorIndex(t, gridMs, lo, mid - 1);
};
